import { useState, useCallback } from 'react';
import { useImageStore } from '@/entities/image/store';

const PROVIDERS = [
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'openrouter', label: 'OpenRouter' },
  { value: 'custom', label: 'Custom' },
];

export default function LLMPanel() {
  const [activeTab, setActiveTab] = useState<'ai' | 'generic'>('ai');
  const llmConfig = useImageStore((s) => s.llmConfig);
  const setLLMConfig = useImageStore((s) => s.setLLMConfig);
  const showLLMPanel = useImageStore((s) => s.showLLMPanel);
  const setShowLLMPanel = useImageStore((s) => s.setShowLLMPanel);
  const generateLLMExif = useImageStore((s) => s.generateLLMExif);
  const addNotification = useImageStore((s) => s.addNotification);
  const llmLoading = useImageStore((s) => s.llmLoading);
  const activeImagePath = useImageStore((s) => s.activeImagePath);

  const [provider, setProvider] = useState(llmConfig.provider);
  const [apiKey, setApiKey] = useState(llmConfig.apiKey);
  const [model, setModel] = useState(llmConfig.model);
  const [baseUrl, setBaseUrl] = useState(llmConfig.baseUrl || '');
  const [promptTemplate, setPromptTemplate] = useState(llmConfig.promptTemplate || `Analyze this image and extract EXIF metadata.\n\nReturn STRICT JSON only (no markdown, no explanation):\n{\n  "title": "...",\n  "description": "...",\n  "keywords": ["tag1", "tag2"],\n  "location": {\n    "name": "...",\n    "latitude": "...",\n    "longitude": "..."\n  }\n}\n\nKeywords should be relevant, concise, English, Lightroom-compatible.`);

  const handleSave = useCallback(async () => {
    await setLLMConfig({ provider, apiKey, model, baseUrl, promptTemplate });
    addNotification('success', 'Configuration saved.');
  }, [setLLMConfig, provider, apiKey, model, baseUrl, promptTemplate, addNotification]);

  const handleGenerate = useCallback(async () => {
    if (!activeImagePath) {
      addNotification('warning', 'Select an image first.');
      return;
    }
    const payload = await generateLLMExif(activeImagePath);
    if (payload) {
      addNotification('success', `Generated EXIF for selected image.`);
    }
  }, [activeImagePath, generateLLMExif, addNotification]);

  if (!showLLMPanel) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 w-full max-w-2xl max-h-[80vh] flex flex-col rounded-xl shadow-2xl border border-gray-700">
        {/* Header with Tabs */}
        <div className="flex items-center justify-between border-b border-gray-700 px-6 pt-4 pb-0">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('ai')}
              className={`px-3 py-2 text-sm font-medium transition border-b-2 ${
                activeTab === 'ai'
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              AI Settings
            </button>
            <button
              onClick={() => setActiveTab('generic')}
              className={`px-3 py-2 text-sm font-medium transition border-b-2 ${
                activeTab === 'generic'
                  ? 'border-purple-500 text-purple-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              Generic
            </button>
          </div>
          <button
            onClick={() => setShowLLMPanel(false)}
            className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-gray-700 transition"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === 'ai' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-400">Provider</label>
                  <select
                    value={provider}
                    onChange={(e) => setProvider(e.target.value as 'openai' | 'anthropic' | 'openrouter' | 'custom')}
                    className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                  >
                    {PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400">Model</label>
                  <input
                    type="text"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-400">API Key</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                    placeholder="sk-..."
                  />
                </div>
                {provider === 'custom' && (
                  <div className="col-span-2">
                    <label className="text-xs text-gray-400">Base URL</label>
                    <input
                      type="text"
                      value={baseUrl}
                      onChange={(e) => setBaseUrl(e.target.value)}
                      className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
                      placeholder="https://api.example.com/v1/chat/completions"
                    />
                  </div>
                )}
                <div className="col-span-2">
                  <label className="text-xs text-gray-400">Prompt Template</label>
                  <textarea
                    value={promptTemplate}
                    onChange={(e) => setPromptTemplate(e.target.value)}
                    rows={6}
                    className="w-full mt-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-xs font-mono focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'generic' && (
            <div className="flex items-center justify-center h-48 text-gray-500 text-sm border-2 border-dashed border-gray-700 rounded-lg">
              Generic settings placeholder. Coming soon.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-700 bg-gray-800/50 rounded-b-xl">
          <button
            onClick={handleGenerate}
            disabled={llmLoading || !activeImagePath}
            className={`mr-2 px-4 py-2 rounded text-sm font-medium transition ${
              llmLoading || !activeImagePath
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {llmLoading ? 'Generating...' : 'Generate EXIF'}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm font-medium transition"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  );
}
